/**
 * ToolCallBlock — a single tool call rendered as a quiet, collapsible row.
 *
 * Shares the extended-thinking panel's chrome (thin border, soft bg, muted
 * text, a left chevron) so tool activity reads as subtle background work rather
 * than a loud, color-accented event:
 *   Running → pulsing dots
 *   Done    → muted check + duration
 * Click the row to expand the Input / Output detail.
 */

import { useState } from "react";
import type { ToolCallState } from "@/api/types";
import { useSubagentDrillStore } from "@/stores/subagentDrillStore";
import { toolMeta } from "@/theme";

interface Props {
  toolCall: ToolCallState;
}

export function ToolCallBlock({ toolCall }: Props) {
  const [expanded, setExpanded] = useState(false);
  const drillInto = useSubagentDrillStore((s) => s.open);

  // Parse MCP tool name: "mcp__<server>__<tool>" → server + tool
  const mcpMatch = toolCall.tool_name.match(/^mcp__(\w+)__(.+)$/);
  const mcpServer = mcpMatch?.[1] ?? null;
  const rawName = mcpMatch?.[2] ?? toolCall.tool_name;

  // Pretty server name for the "from ..." suffix
  const serverLabel: Record<string, string> = {
    cowork: "OmniWerk",
    nbyula: "Nbyula",
  };
  const fromLabel = mcpServer ? serverLabel[mcpServer] ?? mcpServer : null;

  const meta = toolMeta[rawName] ?? toolMeta[toolCall.tool_name] ?? {
    icon: "🔧",
    label: rawName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  };

  const isRunning = toolCall.status === "running";
  const durationMs = toolCall.duration_ms ?? 0;

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  // A ``Task``/``Agent`` card stands for a whole sub-agent run. Its raw
  // input/output JSON says little, so offer a drill-in to that sub-agent's own
  // stream instead (schema v35 keeps those rows out of the main transcript).
  const isSubagent = toolCall.tool_name === "Task" || toolCall.tool_name === "Agent";

  return (
    <div className="rounded-lg border border-space-20 bg-space-10/60 overflow-hidden animate-[slideIn_200ms_ease-out]">
      <div className="flex items-center">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex-1 min-w-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-space-50 hover:text-space transition-colors overflow-hidden"
      >
        {/* Left chevron — the same quiet expand affordance as the thoughts panel */}
        <svg
          className={`w-3 h-3 shrink-0 text-space-40 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>

        <span className="shrink-0">{meta.icon}</span>
        <span className="truncate">
          {meta.label}
          {fromLabel && <span className="text-space-40 font-normal"> from {fromLabel}</span>}
        </span>

        {/* Right status — pulsing dots while running, muted check + duration once done */}
        <span className="ml-auto flex items-center gap-1.5 shrink-0 text-space-40">
          {isRunning ? (
            <span className="flex gap-0.5" aria-label="running">
              <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse [animation-delay:-0.3s]" />
              <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse [animation-delay:-0.15s]" />
              <span className="w-1 h-1 rounded-full bg-jove-60 animate-pulse" />
            </span>
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {durationMs > 0 && (
                <span className="tabular-nums font-mono">{formatTime(durationMs)}</span>
              )}
            </>
          )}
        </span>
      </button>

      {/* Sibling, not nested — a button inside a button is invalid HTML. */}
      {isSubagent && (
        <button
          type="button"
          onClick={() => drillInto(toolCall.tool_use_id)}
          className="shrink-0 mr-2 px-2 py-1 text-[10px] font-medium rounded
                     text-jove hover:bg-jove-30 transition-colors"
          title="View this sub-agent's activity"
        >
          View activity
        </button>
      )}
      </div>

      {/* Expanded content — Input / Output detail, separated from the header. */}
      {expanded && (
        <div className="px-3 pb-3 pt-2.5 space-y-2 border-t border-space-20/70 animate-[slideDown_250ms_ease-out]">
          {/* Input */}
          {Object.keys(toolCall.input).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-space-40 font-semibold mb-1">Input</p>
              <pre className="text-xs bg-jove-10 text-space-50 rounded-lg p-2.5 overflow-x-auto font-mono max-h-32 overflow-y-auto leading-relaxed">
                {JSON.stringify(toolCall.input, null, 2)}
              </pre>
            </div>
          )}

          {/* Output */}
          {toolCall.result && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-space-40 font-semibold mb-1">Output</p>
              <pre className="text-xs rounded-lg p-2.5 overflow-x-auto font-mono max-h-48 overflow-y-auto leading-relaxed bg-surface border border-space-30 text-space-50">
                {toolCall.result.length > 2000
                  ? toolCall.result.slice(0, 2000) + "\n... (truncated)"
                  : toolCall.result}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
